// Helpers for unauthenticated, iframe-embeddable public forms. These post via a stateless `fetch`
// (not Inertia's useForm) because an embedded form runs in a third-party iframe where the session
// cookie is blocked — so Inertia's redirect + flash success path can't carry state. No CSRF token
// is sent: the server skips token verification on these endpoints and authorizes by a same-origin
// Origin check instead (a session-tied token is unreliable in a third-party iframe anyway).

// Field errors are keyed by the snake_case param name (matching the multipart payload); form errors
// are non-field failures (network, unexpected server error) with no field to attach to.
export type PublicFormResult =
  | { success: true }
  | { success: false; fieldErrors: Record<string, string[]>; formErrors: string[] };

// Public forms post back to their own page URL.
export function currentFormPath(): string {
  return window.location.pathname.replace(/\/$/, "");
}

// Build a Rails-nested multipart body: scalar fields → `paramKey[field]`, file arrays →
// `paramKey[field][]`. This is the file handling Inertia's useForm does internally, for the
// embedded forms that post via fetch instead.
export function buildFormData(
  paramKey: string,
  fields: Record<string, string>,
  files: Record<string, File[]> = {},
): FormData {
  const body = new FormData();
  for (const [field, value] of Object.entries(fields)) {
    body.append(`${paramKey}[${field}]`, value);
  }
  for (const [field, items] of Object.entries(files)) {
    for (const file of items) body.append(`${paramKey}[${field}][]`, file);
  }
  return body;
}

export async function submitPublicForm(path: string, body: FormData): Promise<PublicFormResult> {
  try {
    const res = await fetch(path, {
      method: "POST",
      headers: { Accept: "application/json" },
      body,
    });
    const data = (await res.json()) as {
      success?: boolean;
      field_errors?: Record<string, string[]>;
      form_errors?: string[];
    };
    if (res.ok && data.success) return { success: true };

    const fieldErrors = data.field_errors ?? {};
    const formErrors = data.form_errors ?? [];
    // Only fall back to a generic message when the server attributed nothing — otherwise the
    // per-field errors stand on their own without a redundant banner.
    if (formErrors.length === 0 && Object.keys(fieldErrors).length === 0) {
      formErrors.push("Something went wrong. Please try again.");
    }
    return { success: false, fieldErrors, formErrors };
  } catch {
    return {
      success: false,
      fieldErrors: {},
      formErrors: ["We couldn't reach the server. Please check your connection and try again."],
    };
  }
}
